import {
  type ButtonHTMLAttributes,
  type ReactNode,
  useState,
} from "react";
import { tokens } from "../../design-system/tokens";

interface ButtonProps {
  id?: string;
  label: ReactNode;
  icon?: ReactNode;
  iconPosition?: "left" | "right";
  onClick?: () => void;
  className?: string;
  disabled?: boolean;
  width?: string | number;
  maxWidth?: string | number;
  size?: "small" | "medium" | "large" | "xl";
  color?:
  | "primary"
  | "secondary"
  | "outline"
  | "outlineSecondary"
  | "outlineAccent"
  | "secondaryLight";
  iconOnly?: boolean;
  loadingAnimation?: boolean;
  /** Native `<button type>` — use `"submit"` inside `<form>` to trigger `onSubmit`. */
  buttonType?: ButtonHTMLAttributes<HTMLButtonElement>["type"];
}

export default function Button({
  id,
  label,
  icon,
  iconPosition = "left",
  onClick,
  className,
  disabled = false,
  width,
  maxWidth,
  size = "large",
  color = "secondary",
  iconOnly = false,
  loadingAnimation = false,
  buttonType = "button",
}: ButtonProps) {
  const [isHovered, setIsHovered] = useState(false);
  const buttonHeight =
    size === "xl" ? "50px" : size === "large" ? "40px" : size === "medium" ? "36px" : "25px";
  const borderRadius =
    size === "small"
      ? tokens.borderRadius.md
      : size === "medium"
        ? tokens.borderRadius.lg
        : tokens.borderRadius.xl;
  const padding =
    size === "xl"
      ? "10px 14px"
      : size === "large"
        ? "8px 12px"
        : size === "medium"
          ? "6px 10px"
          : "5px 12px";
  const typography =
    size === "small"
      ? tokens.typography.buttonSmall
      : size === "xl"
        ? tokens.typography.buttonXl
        : tokens.typography.button;
  const colorStyles =
    color === "primary"
      ? {
        backgroundColor: tokens.buttonColors.primary,
        borderColor: tokens.buttonColors.primary,
        textColor: tokens.colors.secondary,
      }
      : color === "secondaryLight"
        ? {
          backgroundColor: tokens.buttonColors.secondaryLight,
          borderColor: tokens.buttonColors.secondaryLight,
          textColor: tokens.colors.primary,
        }
        : color === "outlineSecondary"
          ? {
            backgroundColor: "transparent",
            borderColor: tokens.colors.text.secondary,
            textColor: tokens.colors.text.secondary,
          }
          : color === "outlineAccent"
            ? {
              backgroundColor: "transparent",
              borderColor: tokens.buttonColors.outlineAccent,
              textColor: tokens.buttonColors.outlineAccent,
            }
            : color === "outline"
              ? {
                backgroundColor: "transparent",
                borderColor: tokens.colors.text.primary,
                textColor: tokens.colors.text.primary,
              }
              : {
                backgroundColor: tokens.buttonColors.secondary,
                borderColor: tokens.buttonColors.secondary,
                textColor: tokens.colors.text.inverse,
              };
  const isSecondary = color === "secondary";
  const isSecondaryLight = color === "secondaryLight";
  const isOutlineSecondary = color === "outlineSecondary";
  const isOutlineAccent = color === "outlineAccent";
  const isOutline = color === "outline";
  const backgroundColor =
    isOutline && isHovered
      ? tokens.colors.surface
      : isOutlineSecondary && isHovered
        ? tokens.colors.surface
        : isOutlineAccent && isHovered
          ? tokens.colors.surface
          : isSecondaryLight && isHovered
            ? tokens.buttonColors.secondaryLightHover
            : isSecondary && isHovered
              ? tokens.buttonColors.secondaryHover
              : colorStyles.backgroundColor;
  const borderColor =
    isOutlineSecondary && isHovered
      ? tokens.buttonColors.outlineSecondaryHover
      : isOutlineAccent && isHovered
        ? tokens.buttonColors.outlineAccent
        : isSecondaryLight && isHovered
          ? tokens.buttonColors.secondaryLightHover
          : isSecondary && isHovered
            ? tokens.buttonColors.secondaryHover
            : colorStyles.borderColor;
  const textColor =
    isOutlineSecondary && isHovered
      ? colorStyles.textColor
      : colorStyles.textColor;
  const finalBackgroundColor = iconOnly ? "transparent" : backgroundColor;
  const finalBorder = iconOnly ? "none" : `1px solid ${borderColor}`;
  const finalPadding = iconOnly ? "0" : padding;
  const finalWidth = iconOnly ? buttonHeight : width;

  const resolvedLineHeight =
    size === "small" ? 1 : typography.lineHeight;
  const resolvedFontWeight =
    size === "small" ? typography.fontWeight : tokens.typography.button.fontWeight;

  const iconNudgeBySize: Partial<Record<NonNullable<ButtonProps["size"]>, string>> = {
    small: "0px",
    medium: "0px",
    large: "0px",
    xl: "0px",
  };
  const labelNudgeBySize: Partial<Record<NonNullable<ButtonProps["size"]>, string>> = {
    small: "1px",
    medium: "1px",
    large: "1px",
    xl: "1px",
  };
  const iconTranslateY = iconNudgeBySize[size];
  const labelTranslateY = labelNudgeBySize[size];

  return (
    <>
      <style>
        {`@keyframes buttonSkeletonShimmer {
            0% { transform: translateX(-120%); }
            100% { transform: translateX(120%); }
          }`}
      </style>
      <button
        id={id}
        type={buttonType}
        onClick={onClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        disabled={disabled}
        className={["ui-elements-button", className].filter(Boolean).join(" ")}
        style={{
          boxSizing: "border-box",
          cursor: disabled ? "not-allowed" : "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "row",
          gap: "6px",
          backgroundColor: finalBackgroundColor,
          border: finalBorder,
          borderRadius,
          padding: finalPadding,
          height: buttonHeight,
          width: finalWidth,
          maxWidth,
          color: textColor,
          fontFamily: typography.fontFamily,
          fontWeight: resolvedFontWeight,
          fontSize: typography.fontSize,
          lineHeight: String(resolvedLineHeight),
          textAlign: "center",
          whiteSpace: "nowrap",
          transition: `background-color ${tokens.transitions.normal}, border-color ${tokens.transitions.normal}, color ${tokens.transitions.normal}`,
          opacity: disabled ? 0.5 : 1,
          position: "relative",
          overflow: "hidden",
        }}
      >
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: iconPosition === "right" ? "row-reverse" : "row",
            gap: size === "small" ? "4px" : size === "xl" ? "8px" : size === "medium" ? "5px" : "6px",
            position: "relative",
            zIndex: 1,
          }}
        >
          {icon ? (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                ...(iconTranslateY ? { transform: `translateY(${iconTranslateY})` } : {}),
              }}
            >
              {icon}
            </span>
          ) : null}
          {iconOnly ? null : (
            <span
              style={{
                ...(size === "small" ? { lineHeight: 1, display: "inline-block" } : {}),
                ...(labelTranslateY ? { transform: `translateY(${labelTranslateY})` } : {}),
              }}
            >
              {label}
            </span>
          )}
        </span>
        {loadingAnimation ? (
          <span
            aria-hidden="true"
            style={{
              position: "absolute",
              inset: 0,
              borderRadius,
              background:
                "linear-gradient(90deg, transparent 20%, rgba(255,255,255,0.38) 50%, transparent 80%)",
              animation: "buttonSkeletonShimmer 1.3s ease-in-out infinite",
              pointerEvents: "none",
              zIndex: 0,
            }}
          />
        ) : null}
      </button>
    </>
  );
}
